import { Stack, Text, Group, TextInput, Button, Card, Box } from '@mantine/core';
import { Search, X, Download } from 'lucide-react';
import { useState, useMemo } from 'react';
import { OneOnOnesTable } from './OneOnOnesTable';
import { useDataLibraryOneOnOnes } from './useDataLibraryOneOnOnes';
import { ONE_ON_ONE_STATUS_OPTIONS } from './oneOnOneColumns';
import { FilterPanel } from '../../common/FilterPanel/FilterPanel';
import type { FilterFieldConfig } from '../../common/FilterPanel/FilterPanel';
import { SortPanel } from '../../common/SortPanel/SortPanel';
import type { SortFieldConfig } from '../../common/SortPanel/SortPanel';
import { GroupPanel } from '../../common/GroupPanel/GroupPanel';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import { deriveGroupFields } from '../../../utils/groupDataByField';
import { ColumnSettingsPopover } from '../../common/ColumnSettingsPopover/ColumnSettingsPopover';
import { ExportModal } from '../../common/ExportModal/ExportModal';
import { exportSummaryProps, splitScreenFilters } from '../../common/ExportModal/filterSummary';
import type { SortState } from '../../../utils/sortTasks';
import type { DataLibraryOneOnOneSummary } from '../../../types';
import { clearThenFocusFilter } from '../../../utils/focusFilterTrigger';

const SORT_FIELDS: SortFieldConfig[] = [
  { value: 'scheduled_start', label: 'Scheduled' },
  { value: 'participant_name', label: 'Participant' },
  { value: 'coach_name', label: 'Coach' },
  { value: 'cohort_name', label: 'Cohort' },
  { value: 'status', label: 'Status' },
];

function SummaryStat({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="lg" fw={700}>{value}</Text>
    </Box>
  );
}

/** MDF-253: counts over the filtered 1:1s, the same rows as the table. */
function OneOnOneSummary({ summary }: { summary: DataLibraryOneOnOneSummary }) {
  return (
    <Card withBorder radius={8} bg="white" p="sm" style={{ boxShadow: 'none' }}>
      <Group gap="xl" wrap="wrap">
        <SummaryStat label="Total 1:1s" value={summary.total} />
        <SummaryStat label="Completed" value={summary.completed} />
        <SummaryStat label="Scheduled" value={summary.scheduled} />
        <SummaryStat label="Needs attendance" value={summary.needs_attendance} />
        <SummaryStat label="Not scheduled" value={summary.not_scheduled} />
        <SummaryStat label="Canceled / no-show" value={summary.canceled + summary.no_show} />
        {summary.withdrawn > 0 && <SummaryStat label="Withdrawn" value={summary.withdrawn} />}
        <SummaryStat
          label="Completion rate"
          value={summary.completion_rate == null ? 'n/a' : `${summary.completion_rate}%`}
        />
      </Group>
      <Text size="xs" c="dimmed" mt={6}>
        Completion rate is completed 1:1s out of completed, no-show and past canceled 1:1s.
        Withdrawn participants are not in the rate.
      </Text>
    </Card>
  );
}

export function DataLibraryOneOnOnesTab({ selectedCohortIds }: { selectedCohortIds: string[] }) {
  const {
    rows,
    groupedRows,
    summary,
    loading,
    totalCount,
    totalPages,
    filterOptions,
    filterParams,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    activeGroup,
    setActiveGroup,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibraryOneOnOnes({ selectedCohortIds });

  const [exportOpen, setExportOpen] = useState(false);
  const groupFields = useMemo(() => deriveGroupFields(columns), [columns]);

  const filterFields: FilterFieldConfig[] = useMemo(
    () => [
      ...(filterOptions.cohorts.length > 1
        ? [{ key: 'cohort', label: 'Cohort', type: 'select' as const, options: filterOptions.cohorts }]
        : []),
      { key: 'coach', label: 'Coach', type: 'select' as const, options: filterOptions.coaches },
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect' as const,
        options: ONE_ON_ONE_STATUS_OPTIONS.map(({ value, label }) => ({ value, label })),
      },
      { key: 'type', label: 'Type', type: 'multiselect' as const, options: filterOptions.types },
    ],
    [filterOptions],
  );

  const activeSortState: SortState | null = sortConfig
    ? { field: sortConfig.key, direction: sortConfig.order === 'ASC' ? 'asc' : 'desc' }
    : null;

  const { cohortId, ...exportFilters } = filterParams;

  return (
    <Stack gap="sm">
      <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
        <Group gap="xs" wrap="wrap">
          <TextInput
            placeholder="Search 1:1s..."
            size="sm"
            leftSection={<Search size={14} />}
            rightSection={
              searchValue ? <X size={14} style={{ cursor: 'pointer' }} onClick={() => setSearchValue('')} /> : null
            }
            value={searchValue}
            onChange={e => setSearchValue(e.currentTarget.value)}
            w={220}
          />
          <FilterPanel
            quickFilters={[]}
            activeQuickFilters={[]}
            onQuickFilterToggle={() => {}}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            onClearAll={clearAllFilters}
            size="sm"
          />
          <SortPanel
            sortFields={SORT_FIELDS}
            activeSort={activeSortState}
            defaultSort={{ field: 'scheduled_start', direction: 'desc' }}
            onSortChange={handleSortPanelChange}
            size="sm"
          />
          <GroupPanel
            groupFields={groupFields}
            activeGroup={activeGroup}
            limitTotal={groupLimitTotal}
            onGroupChange={setActiveGroup}
            size="sm"
          />
          <SavedViewsMenu
            columns={columns}
            onColumnsChange={setColumns}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            sortFields={SORT_FIELDS}
            activeSort={activeSortState}
            onSortChange={handleSortPanelChange}
            groupFields={groupFields}
            activeGroup={activeGroup}
            onGroupChange={setActiveGroup}
            size="sm"
          />
          {hasActiveFilters && (
            <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={clearThenFocusFilter(clearAllFilters)}>
              Clear
            </Button>
          )}
        </Group>

        <Group gap="xs">
          <Text size="sm" c="dimmed">
            {totalCount} 1:1{totalCount !== 1 ? 's' : ''}
          </Text>
          <ColumnSettingsPopover columns={columns} onColumnsChange={setColumns} zIndex={1000} />
          <Button
            variant="outline"
            color="gray"
            bg="white"
            size="sm"
            style={{ borderColor: '#E8E6E3' }}
            leftSection={<Download size={14} />}
            onClick={() => setExportOpen(true)}
          >
            Export
          </Button>
        </Group>
      </Group>

      {summary && <OneOnOneSummary summary={summary} />}

      <OneOnOnesTable
        data={rows}
        groupedData={groupedRows}
        columns={visibleColumns}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <ExportModal
        opened={exportOpen}
        onClose={() => setExportOpen(false)}
        entityType="survey_responses"
        contextType="global"
        scopeContext={{ cohortIds: selectedCohortIds, surveyKind: 'one_on_ones' }}
        filterContext={{ ...(cohortId && { cohortIds: [cohortId] }), ...exportFilters }}
        {...exportSummaryProps(
          splitScreenFilters(
            { search: exportFilters.search, fields: filterFields, rows: activeFilters },
            ['search', 'cohort', 'coach', 'status', 'type'],
          ),
        )}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        gridColumns={columns}
        recordCount={totalCount}
        entityLabel="1:1s"
        gridColumnsOnly
      />
    </Stack>
  );
}
